// =============================================================================
// SIREPPAR - Página de Listado de Expedientes
// =============================================================================

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import DataTable, { Column } from '../../components/common/DataTable';
import { expedienteService } from '../../services/api';
import { TIPO_TRAMITE_LABELS } from '../../utils/tiposExpediente';

// =============================================================================
// Tipos - Coincidentes con el backend
// =============================================================================

interface Expediente {
  id: string;
  numero: string;
  tipo: string;
  asunto: string;
  estado: string;
  fechaCreacion: string;
  fechaPresentacion?: string;
  fechaVencimiento?: string;
  partido?: {
    denominacion: string;
    sigla?: string;
  };
  asignado?: {
    nombre: string;
    apellido: string;
  };
}

// =============================================================================
// Constantes - Estados del backend
// =============================================================================

// Todos los estados del enum EstadoExpediente (schema.prisma), en el orden real
// del circuito. Las etiquetas y colores son los mismos que usan /gestion y el
// detalle del expediente, para que un estado no se llame distinto según la pantalla.
const estadoConfig: Record<string, { color: string; label: string }> = {
  BORRADOR: { color: 'bg-gray-100 text-gray-800', label: 'Borrador' },
  PRESENTADO: { color: 'bg-blue-100 text-blue-800', label: 'Presentado' },
  EN_ADMISIBILIDAD: { color: 'bg-purple-100 text-purple-800', label: 'En Admisibilidad' },
  INTIMADO: { color: 'bg-orange-100 text-orange-800', label: 'Intimado' },
  EN_ANALISIS: { color: 'bg-yellow-100 text-yellow-800', label: 'En Análisis' },
  OBSERVADO: { color: 'bg-orange-100 text-orange-800', label: 'Observado' },
  // Circuito de resolución cuando venció el plazo y el partido nunca subsanó.
  NO_PRESENTADO: { color: 'bg-red-100 text-red-800', label: 'No Presentado' },
  EN_SECRETARIO: { color: 'bg-indigo-100 text-indigo-800', label: 'En Secretario' },
  EN_TEP: { color: 'bg-cyan-100 text-cyan-800', label: 'En el TEP' },
  REC_PROVISORIO: { color: 'bg-teal-100 text-teal-800', label: 'Reconocimiento provisorio' },
  VIGENTE: { color: 'bg-green-100 text-green-800', label: 'Vigente' },
  APROBADO: { color: 'bg-green-100 text-green-800', label: 'Aprobado' },
  RECHAZADO: { color: 'bg-red-100 text-red-800', label: 'Rechazado' },
  CADUCO: { color: 'bg-gray-200 text-gray-600', label: 'Caduco' },
  EXTINGUIDO: { color: 'bg-red-100 text-red-800', label: 'Extinguido' },
  ARCHIVADO: { color: 'bg-gray-200 text-gray-600', label: 'Archivado' },
  // Legacy: nombre anterior de EN_SECRETARIO. Se mantiene para que el badge de los
  // expedientes sin migrar siga renderizando, pero NO se ofrece como filtro: sería
  // un segundo "En Secretario" en el desplegable.
  PENDIENTE_RESOLUCION: { color: 'bg-indigo-100 text-indigo-800', label: 'En Secretario' },
};

// Estados que se ofrecen en el filtro, en orden de circuito. Se lista explícito
// (en vez de recorrer estadoConfig) para poder excluir el alias legacy.
const ESTADOS_FILTRO = [
  'BORRADOR', 'PRESENTADO', 'EN_ADMISIBILIDAD', 'INTIMADO', 'EN_ANALISIS', 'OBSERVADO',
  'NO_PRESENTADO', 'EN_SECRETARIO', 'EN_TEP', 'REC_PROVISORIO', 'VIGENTE',
  'APROBADO', 'RECHAZADO', 'CADUCO', 'EXTINGUIDO', 'ARCHIVADO',
];

const tipoLabels = TIPO_TRAMITE_LABELS;

// =============================================================================
// Componente
// =============================================================================

export default function ExpedientesListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const partidoIdParam = searchParams.get('partidoId');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  // El acordeón de filtros arranca desplegado (reemplaza los chips de estado).
  const [showFilters, setShowFilters] = useState(true);

  // Query
  const { data, isLoading, error } = useQuery({
    queryKey: ['expedientes', page, pageSize, search, estadoFilter, tipoFilter, partidoIdParam],
    queryFn: async () => {
      const params: Record<string, any> = { page, pageSize };
      if (search) params.search = search;
      if (estadoFilter) params.estado = estadoFilter;
      if (tipoFilter) params.tipo = tipoFilter;
      if (partidoIdParam) params.partidoId = partidoIdParam;
      
      const response = await expedienteService.list(params);
      return response.data;
    },
  });

  // Verificar si está vencido
  const isVencido = (fechaVencimiento?: string, estado?: string) => {
    if (!fechaVencimiento) return false;
    if (['ARCHIVADO', 'APROBADO', 'RECHAZADO'].includes(estado || '')) return false;
    return new Date(fechaVencimiento) < new Date();
  };

  // Columnas
  const columns: Column<Expediente>[] = [
    {
      key: 'numero',
      header: 'Número',
      width: '150px',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <span className="font-mono font-medium text-gray-900">{value}</span>
          {isVencido(row.fechaVencimiento, row.estado) && (
            <ExclamationTriangleIcon className="h-4 w-4 text-red-500" title="Vencido" />
          )}
        </div>
      ),
    },
    {
      key: 'tipo',
      header: 'Tipo',
      width: '180px',
      render: (value) => (
        <span className="text-sm">{tipoLabels[value] || value}</span>
      ),
    },
    {
      key: 'asunto',
      header: 'Asunto',
      render: (value, row) => (
        <div>
          <p className="font-medium text-gray-900 truncate max-w-xs">{value}</p>
          {row.partido && (
            <p className="text-sm text-gray-500">
              {row.partido.sigla ? `${row.partido.sigla} - ` : ''}{row.partido.denominacion}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'estado',
      header: 'Estado',
      width: '140px',
      render: (value) => {
        const config = estadoConfig[value] || estadoConfig.BORRADOR;
        return (
          <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'fechaVencimiento',
      header: 'Vencimiento',
      width: '130px',
      render: (value, row) => {
        if (!value) return <span className="text-gray-400">-</span>;
        const fecha = new Date(value);
        const vencido = isVencido(value, row.estado);
        return (
          <div className={`flex items-center gap-1 ${vencido ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
            <ClockIcon className="h-4 w-4" />
            <span className="text-sm">{fecha.toLocaleDateString('es-AR')}</span>
          </div>
        );
      },
    },
    {
      key: 'asignado',
      header: 'Asignado a',
      width: '150px',
      render: (value) => value ? (
        <span className="text-sm">{value.nombre} {value.apellido}</span>
      ) : (
        <span className="text-gray-400 text-sm">Sin asignar</span>
      ),
    },
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setSearchInput('');
    setEstadoFilter('');
    setTipoFilter('');
    setPage(1);
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error al cargar los expedientes</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Expedientes</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gestión de expedientes y trámites
          </p>
        </div>
      </div>

      {/* Búsqueda y filtros avanzados */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Buscar por número o asunto..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </form>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg transition ${
              showFilters ? 'border-primary-500 text-primary-600 bg-primary-50' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FunnelIcon className="h-5 w-5" />
            Filtros
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select
                value={estadoFilter}
                onChange={(e) => { setEstadoFilter(e.target.value); setPage(1); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Todos</option>
                {ESTADOS_FILTRO.map((key) => (
                  <option key={key} value={key}>{estadoConfig[key].label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
              <select
                value={tipoFilter}
                onChange={(e) => { setTipoFilter(e.target.value); setPage(1); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Todos</option>
                {Object.entries(tipoLabels).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button onClick={clearFilters} className="text-sm text-gray-600 hover:text-gray-800 underline">
                Limpiar filtros
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tabla. Sin rowClassName: no se tiñe la fila cuando el expediente está
          vencido — eso ya se lee en la columna Vencimiento, y pintar la fila
          entera de rojo tapaba el resto de la información. */}
      <DataTable
        data={data?.data || []}
        columns={columns}
        loading={isLoading}
        emptyMessage="No se encontraron expedientes"
        onRowClick={(row) => navigate(`/expedientes/${row.id}`)}
        pagination={{
          page,
          pageSize,
          total: data?.meta?.totalItems || data?.meta?.total || data?.pagination?.total || 0,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        }}
      />
    </div>
  );
}
